import React, { forwardRef } from 'react';
import TextField from '@material-ui/core/TextField';
import { makeStyles } from '@material-ui/core/styles';
import { mergeClassNames } from '../../../utils';
import { PRINTER_NUMBER_MAX_LENGTH } from '../../../constants';

const useStyles = makeStyles({
  root: {
    width: '100%',
    '@media (min-width:769px)': {
      width: '54.5%',
    },
    '& .MuiFilledInput-root': {
      backgroundColor: 'transparent',
      border: '1px solid var(--grey-11)',
      borderRadius: 4,
    },
    '& .MuiFilledInput-underline:before': {
      borderBottom: 'none',
    },
    '& .MuiFilledInput-underline:after': {
      borderBottom: 'none',
    },
    '& .MuiInputLabel-filled': {
      fontSize: 'var(--font-size-14)',

      '@media (min-width:769px)': {
        fontSize: 'var(--font-size-16)',
      },
    },
    '& .MuiInputLabel-filled.MuiInputLabel-shrink': {
      fontSize: 'var(--font-size-12)',
      color: 'var(--grey-08)',
    },
    '&.errorPrinter .MuiFilledInput-root': {
      borderColor: 'var(--red-02)',
    },
  },
});

type Props = {
  id: string;
  label: string;
  value: string;
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  error?: boolean | null;
  maxLength?: number;
  inputRootClass?: string;
  onSubmit?: () => void;
};

const PrinterNumberField = forwardRef<HTMLInputElement, Props>(({
  id,
  label,
  value,
  onChange,
  error,
  maxLength = PRINTER_NUMBER_MAX_LENGTH,
  inputRootClass,
  onSubmit,
}, ref) => {
  const classes = useStyles();

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const inputValue = e.target.value;
    const numericValue = inputValue.replace(/[^0-9]/g, '').slice(0, maxLength);
    const syntheticEvent = {
      ...e,
      target: {
        ...e.target,
        value: numericValue,
      },
    } as React.ChangeEvent<HTMLInputElement>;
    onChange(syntheticEvent);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    // Handle Enter key for form submission
    if (e.key === 'Enter' && onSubmit) {
      e.preventDefault();
      onSubmit();
      return;
    }

    // Allow: backspace, delete, tab, escape, enter, home, end, left, right arrows
    const allowedKeys = [
      'Backspace',
      'Delete',
      'Tab',
      'Escape',
      'Enter',
      'Home',
      'End',
      'ArrowLeft',
      'ArrowRight',
      'ArrowUp',
      'ArrowDown',
    ];

    // Allow: Ctrl+A, Ctrl+C, Ctrl+V, Ctrl+X, Ctrl+Z
    if (e.ctrlKey && ['a', 'c', 'v', 'x', 'z'].includes(e.key.toLowerCase())) {
      return;
    }

    // Allow: digits 0-9
    if (e.key >= '0' && e.key <= '9') {
      return;
    }

    // Allow special keys
    if (allowedKeys.includes(e.key)) {
      return;
    }

    // Block everything else
    e.preventDefault();
  };

  const handlePaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
    e.preventDefault();
    const pastedText = e.clipboardData.getData('text');
    const numericValue = pastedText.replace(/[^0-9]/g, '').slice(0, maxLength);
    const syntheticEvent = {
      target: {
        value: numericValue,
      },
    } as React.ChangeEvent<HTMLInputElement>;

    onChange(syntheticEvent);
  };

  return (
    <TextField
      id={id}
      label={label}
      variant="filled"
      type="number"
      fullWidth
      className={mergeClassNames(classes.root, error && 'errorPrinter', inputRootClass)}
      value={value}
      onChange={handleChange}
      onKeyDown={handleKeyDown}
      onPaste={handlePaste}
      inputRef={ref}
      inputProps={{
        inputMode: 'numeric',
        pattern: '\\d*',
      }}
    />
  );
});

PrinterNumberField.displayName = 'PrinterNumberField';

export default PrinterNumberField;
